import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '@env/environment';
import { take } from 'rxjs';
import { TabsComponent } from '@app/shared/custom-component/tabs/tabs.component';
import { TabItem } from '@app/shared/custom-component/tabs/tabs.interface';
import {
  EducatorDashboardApplication,
  EducatorDashboardDataHttpResponse,
  EducatorDashboardDataRow,
  EducatorDashboardLicenseDtl
} from './educator-dashboard-data.model';
import { LookupUserPersonalTabComponent } from './tabs/lookup-user-personal-tab/lookup-user-personal-tab.component';
import { LookupUserLogsTabComponent } from './tabs/lookup-user-logs-tab/lookup-user-logs-tab.component';
import { LookupUserPlaceholderTabComponent } from './tabs/lookup-user-placeholder-tab/lookup-user-placeholder-tab.component';
import { LookupUserCommunicationTabComponent } from './tabs/lookup-user-communication-tab/lookup-user-communication-tab.component';
import { LookupUserLegacyDataTabComponent } from './tabs/lookup-user-legacy-data-tab/lookup-user-legacy-data-tab.component';
import { LookupUserActivitiesTabComponent } from './tabs/lookup-user-activities-tab/lookup-user-activities-tab.component';
import { LookupUserExperienceTabComponent } from './tabs/lookup-user-experience-tab/lookup-user-experience-tab.component';

import { LookupUserEducationTabComponent } from './tabs/lookup-user-education-tab/lookup-user-education-tab.component';
import { LookupUserLicenseTabComponent } from './tabs/lookup-user-license-tab/lookup-user-license-tab.component';
import { CommonService } from '@app/shared/services/common.service';
import { LocalService } from '@core/services/local.service';
import { LOOKUP_USER_ACTIVE_TAB_KEY, LOOKUP_USER_RETURN_URL_KEY } from '@app/shared/services/application-open-from-grid.service';
import { resolveLookupApplicationStatusDisplay } from './lookup-application-status.util';
import { ManageProfileService } from '@app/pages/manage-profile/manage-profile.service';

@Component({
  selector: 'app-lookup-user-detail',
  standalone: true,
  imports: [
    CommonModule,
    TabsComponent,
    LookupUserPersonalTabComponent,
    LookupUserLogsTabComponent,
    LookupUserPlaceholderTabComponent,
    LookupUserCommunicationTabComponent,
    LookupUserLegacyDataTabComponent,
    LookupUserActivitiesTabComponent,
    LookupUserExperienceTabComponent,
    LookupUserLicenseTabComponent,
    LookupUserEducationTabComponent,

  ],
  templateUrl: './lookup-user-detail.component.html',
  styleUrls: ['./lookup-user-detail.component.scss']
})
export class LookupUserDetailComponent implements OnInit {
  personId: string | null = null;

  tabs: TabItem[] = [
    { key: 'personal', label: 'Personal' },
    { key: 'activities', label: 'Activities' },
    { key: 'license', label: 'License' },
    { key: 'experience', label: 'Experience' },
    { key: 'education', label: 'Education' },
    { key: 'communication', label: 'Communication' },
    { key: 'logs', label: 'Logs' },
    { key: 'legacyData', label: 'Legacy Data' }
  ];

  activeTab = 'personal';
  educatorDashboardRow: EducatorDashboardDataRow | null = null;
  /** Set when user follows Application ID from Transaction History (until License tab is implemented). */
  licenseTabContextLine: string | null = null;

  /** Placeholder for empty overview cells (U+2013 en dash, not ASCII hyphen). Not used for Application Status. */
  readonly overviewEnDash = '\u2013';

  private readonly noPendingAppStatus = 'No Pending Transactions';
  resolvedLicenseStatus: 'Active' | 'Inactive' | null = null;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private http: HttpClient,
    private commonService: CommonService,
    private localService: LocalService,
     private manageProfileService: ManageProfileService
  ) {}

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      this.personId = params.get('personId');
      this.restoreActiveTabFromSession(this.personId);
      this.loadEducatorDashboardData();
    });
  }

  /** Restore tab after Close from application (one-shot session key set by ApplicationOpenFromGridService). */
  private restoreActiveTabFromSession(personId: string | null): void {
    const raw = this.localService.getItem(LOOKUP_USER_ACTIVE_TAB_KEY);
    this.localService.removeItem(LOOKUP_USER_ACTIVE_TAB_KEY);
    if (!raw || !personId) {
      return;
    }
    let parsed: { personId?: unknown; tab?: unknown } | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (!parsed || String(parsed.personId ?? '') !== String(personId)) {
      return;
    }
    const candidate = String(parsed.tab ?? '');
    if (this.tabs.some(t => t.key === candidate)) {
      this.activeTab = candidate;
    }
  }

  /** Refetch dashboard row (e.g. after name change / prior name save from Personal tab). */
  refreshEducatorDashboard(): void {
    this.loadEducatorDashboardData();
  }

  private loadEducatorDashboardData(): void {
    const personId = this.personId;
    if (!personId) {
      this.educatorDashboardRow = null;
      return;
    }
    const payload = { personId: Number(personId) };
    const url = this.buildEducatorDashboardUrl();
    const headers = this.buildJsonHeaders();
    this.http
      .post<EducatorDashboardDataHttpResponse>(url, payload, { headers })
      .pipe(take(1))
      .subscribe({
        next: response => {
          const payloadInner = response?.data;
          if (
            response?.status === 'success' &&
            payloadInner?.success &&
            Array.isArray(payloadInner.data) &&
            payloadInner.data.length > 0
          ) {
            this.educatorDashboardRow = payloadInner.data[0];
          } else {
            this.educatorDashboardRow = null;
          }
           this.loadResolvedLicenseStatus();
        },
        error: err => console.error('[LookupUserDetail] educator-dashboard-data error:', err)
      });
  }

  
  onSectionRefresh(data) {
    console.log(data)
    // ✅ update the parent data
    this.educatorDashboardRow = data.refreshedData
  }

  private buildEducatorDashboardUrl(): string {
    const path = '/search/educator-dashboard-data';
    if (typeof window !== 'undefined' && window.location?.hostname === 'localhost') {
      const localApi = 'http://localhost:4050/api';
      const stored = sessionStorage.getItem('apiHost');
      if (stored && stored.includes('localhost')) {
        return stored.replace(/\/$/, '') + path;
      }
      return localApi + path;
    }
    const base = (environment as any).apiHost || '';
    return (base.endsWith('/') ? base.slice(0, -1) : base) + path;
  }

  private buildJsonHeaders(): HttpHeaders {
    let h = new HttpHeaders()
      .set('Accept', 'application/json')
      .set('Content-Type', 'application/json');
    const token = sessionStorage.getItem('jwt-token');
    if (token) h = h.set('Authorization', `Bearer ${token}`);
    return h;
  }

  onTabSelected(tabKey: string): void {
    this.activeTab = tabKey;
  }

  onOpenApplicationLicenseTab(payload: { internalApplicationId: string; displayApplicationId: string }): void {
    const display = payload?.displayApplicationId?.trim() || payload?.internalApplicationId || '';
    this.licenseTabContextLine = display
      ? `Selected application: ${display} (internal id: ${payload.internalApplicationId}). License content coming soon.`
      : null;
    this.activeTab = 'license';
  }

  close(): void {
    const returnUrl = this.localService.getItem(LOOKUP_USER_RETURN_URL_KEY);
    if (returnUrl) {
      this.localService.removeItem(LOOKUP_USER_RETURN_URL_KEY);
    }
    this.router.navigate([returnUrl || '/pages/lookup-search']);
  }

  navigateToMessageInbox(): void {
    this.router.navigate(['/pages/message-center']);
  }

  /** En-dash placeholder for overview fields (not Application Status). */
  private overviewDash(value: unknown): string {
    if (value === null || value === undefined) {
      return this.overviewEnDash;
    }
    const s = String(value).trim();
    return s.length > 0 ? s : this.overviewEnDash;
  }

  private profileObject(): Record<string, unknown> | null {
    const row = this.educatorDashboardRow;
    if (!row?.p_profile) return null;
    const p = row.p_profile;
    if (Array.isArray(p) && p.length > 0) return p[0] as Record<string, unknown>;
    if (typeof p === 'object' && !Array.isArray(p)) return p as Record<string, unknown>;
    return null;
  }

  private firstLicense(): EducatorDashboardLicenseDtl | null {
    const list = this.educatorDashboardRow?.p_license_dtls;
    if (!Array.isArray(list) || list.length === 0) return null;
    const latestRecord = list.reduce((latest, current) => {
      const currentExpiry = new Date(current.expiry_date ?? 0);
      const latestExpiry = new Date(latest.expiry_date ?? 0);
      return currentExpiry > latestExpiry ? current : latest;
    });

    return latestRecord;
  }

  private firstApplication(): EducatorDashboardApplication | null {
    const list = this.educatorDashboardRow?.p_application;
    if (!Array.isArray(list) || list.length === 0) return null;
    return list[0];
  }

  private strTrim(v: unknown): string {
    if (v == null || v === '') return '';
    return String(v).trim();
  }

  /**
   * Legal name on record: p_profile first, middle, last, suffix (spaces only, no comma), else person_name.
   */
  get nameOnRecord(): string {
    const prof = this.profileObject() as Record<string, unknown> | null;
    if (!prof) {
      return this.overviewEnDash;
    }
    const first = this.strTrim(prof['firstname']);
    const middle = this.strTrim(prof['middlename']);
    const last = this.strTrim(prof['lastname']);
    const suffix = this.strTrim(prof['legal_suffix_display']);
    const parts = [first, middle, last, suffix].filter(s => s.length > 0);
    if (parts.length > 0) {
      return parts.join(' ').replace(/\s+/g, ' ').trim();
    }
    const pn = this.strTrim(prof['person_name']);
    return pn.length > 0 ? pn : this.overviewEnDash;
  }

  /** All non-empty license numbers from p_license_dtls, or en dash. */
  get licenseNumber(): string {
    const list = this.educatorDashboardRow?.p_license_dtls;
    if (!Array.isArray(list) || list.length === 0) {
      return this.overviewEnDash;
    }
    const nums: string[] = [];
    for (const row of list) {
      if (row?.license_number == null) continue;
      const n = String(row.license_number).trim();
      if (n.length > 0) {
        nums.push(n);
      }
    }
    if (nums.length === 0) {
      return this.overviewEnDash;
    }
    return [...new Set(nums)].join(', ');
  }

  /** First p_license_dtls row: license status label from lookup 313 (`license_status`), else en-dash. */
  // get licenseStatus(): string {
  //   const lic = this.firstLicense();
  //   if (!lic) {
  //     return this.overviewEnDash;
  //   }
  //   const label = this.strTrim(lic.license_status);
  //   return label.length > 0 ? label : this.overviewEnDash;
  // }

  /** License status: 'Active' if ANY license/kind is active and not expired, 'Inactive' if ALL are inactive/expired. */

  // old code
  
// get licenseStatus(): string {
//   const licenseDtls = this.educatorDashboardRow?.p_license_dtls;
//   const licenseKinds = this.educatorDashboardRow?.p_licensekind;

//   // Check p_license_dtls array
//   const licenseDtlsAllInactive = Array.isArray(licenseDtls) && licenseDtls.length > 0
//     ? licenseDtls.every((row: any) => {
//         const status = (row?.license_status || '').toLowerCase();
//         const isExpired = row?.expiry_date ? new Date(row.expiry_date) < new Date() : true;
//         return status !== 'active' || isExpired;
//       })
//     : true;

//   // Check p_licensekind array
//   const licenseKindsAllInactive = Array.isArray(licenseKinds) && licenseKinds.length > 0
//     ? licenseKinds.every((row: any) => {
//         const status = (row?.cd_endo_status || '').toLowerCase();
//         const isExpired = row?.expiry_date ? new Date(row.expiry_date) < new Date() : true;
//         return status !== 'active' || isExpired;
//       })
//     : true;

//   // If both sections are all inactive/expired, return Inactive; otherwise Active
//   if (licenseDtlsAllInactive && licenseKindsAllInactive) {
//     return 'Inactive';
//   }
//   if (Array.isArray(licenseDtls) && licenseDtls.length > 0) {
//     return 'Active';
//   }
//   if (Array.isArray(licenseKinds) && licenseKinds.length > 0) {
//     return 'Active';
//   }
//   return this.overviewEnDash;
// }


get licenseStatus(): string {
  return this.resolvedLicenseStatus ?? this.overviewEnDash;
}


  /** From first p_license_dtls expiry_date (formatted when parseable, else as-is). */
  get licenseExpiration(): string {
    const raw = this.firstLicense()?.expiry_date;
    if (raw == null || String(raw).trim() === '') {
      return this.overviewEnDash;
    }
    const s = String(raw).trim();
    if (s.includes('-')) {
      return this.commonService.formatDate(s) || this.overviewDash(s);
    }
    if (s.includes('/')) {
      const d = new Date(s);
      return Number.isNaN(d.getTime()) ? s : this.commonService.getFormatedDate(d);
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? s : this.commonService.getFormatedDate(d);
  }

  get applicationStatus(): string {
    const list = this.educatorDashboardRow?.p_application;
    if (!Array.isArray(list) || list.length === 0) {
      return this.noPendingAppStatus;
    }
    const v = resolveLookupApplicationStatusDisplay(this.firstApplication(), this.educatorDashboardRow);
    return v == null ? '' : String(v).trim();
  }

  get applicationId(): string {
    return this.overviewDash(this.firstApplication()?.app_id);
  }

  get applicationType(): string {
    return this.overviewDash(this.firstApplication()?.app_type);
  }

  get licensePath(): string {
    const fromApp = this.firstApplication()?.license_path;
    if (fromApp == null || String(fromApp).trim() === '') {
      return this.overviewEnDash;
    }
    return this.overviewDash(fromApp);
  }

private loadResolvedLicenseStatus(): void {
  if (!this.personId) return;

  this.manageProfileService.getLicenseKindEndorsementsByPersonId(this.personId).subscribe({
    next: (res: any) => {
      const data = res?.data?.data?.data || [];

      if (!Array.isArray(data) || data.length === 0) {
        this.resolvedLicenseStatus = null;
        return;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const allActive = data.every((item: any) => {
        const dueDateObj = new Date(item.expiration_date);
        dueDateObj.setHours(0, 0, 0, 0);
        const computedStatus = dueDateObj < today
          ? 'Inactive'
          : (item.endorsement_status || '').toLowerCase() === 'active'
            ? 'Active'
            : 'Pending';
        return computedStatus === 'Active';
      });

      const licenseExpiryRaw = this.firstLicense()?.expiry_date;
      const licenseExpiry = licenseExpiryRaw
        ? new Date(new Date(licenseExpiryRaw).setHours(0, 0, 0, 0))
        : null;
      const isLicenseExpired = licenseExpiry ? licenseExpiry < today : false;

      this.resolvedLicenseStatus = (allActive && !isLicenseExpired) ? 'Active' : 'Inactive';
    },
    error: (err) => {
      console.error('[LookupUserDetail] Error loading license kind endorsements:', err);
    }
  });
}

// onLicenseStatusChanged(status: string): void {
//   this.calculatedExtensionStatus = (status === 'Inactive' ? 'Inactive' : 'Active') as 'Active' | 'Inactive';
//   console.log('[LookupUserDetail] Calculated Extension Status Updated:', this.calculatedExtensionStatus);
// }
}
